/**
 * Authentication Context (Task 034)
 *
 * Provides authentication state and methods throughout the application.
 * Supports demo mode when Firebase credentials are not configured.
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { isDemoMode, auth, db } from '../services/firebase';
import { mockUser, mockUserProfile } from '../services/mockData';
import { USER_ROLES, STORAGE_KEYS } from '../utils/constants';

// Import Firebase methods directly - they'll only be used when not in demo mode
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  updateProfile,
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';

// Create the context
const AuthContext = createContext(null);

/**
 * Authentication Provider Component
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(isDemoMode ? mockUser : null);
  const [userProfile, setUserProfile] = useState(isDemoMode ? mockUserProfile : null);
  const [userRole, setUserRole] = useState(isDemoMode ? mockUserProfile.role : null);
  const [orgId, setOrgId] = useState(isDemoMode ? mockUserProfile.orgId : null);
  const [loading, setLoading] = useState(!isDemoMode);
  const [error, setError] = useState(null);

  /**
   * Fetch user profile from Firestore (only in non-demo mode)
   */
  const fetchUserProfile = useCallback(async (uid) => {
    if (isDemoMode) {
      return mockUserProfile;
    }

    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const profile = userDoc.data();
        setUserProfile(profile);
        setUserRole(profile.role || USER_ROLES.EMPLOYEE);
        setOrgId(profile.org_id || null);
        return profile;
      }
      return null;
    } catch (err) {
      console.error('Error fetching user profile:', err);
      return null;
    }
  }, []);

  /**
   * Listen for auth state changes (only in non-demo mode)
   */
  useEffect(() => {
    if (isDemoMode) {
      // In demo mode, we're already "logged in"
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        await fetchUserProfile(firebaseUser.uid);
      } else {
        setUser(null);
        setUserProfile(null);
        setUserRole(null);
        setOrgId(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [fetchUserProfile]);

  /**
   * Sign in with email and password
   */
  const login = useCallback(async (email, password) => {
    if (isDemoMode) {
      // Demo mode: accept any credentials
      setUser(mockUser);
      setUserProfile(mockUserProfile);
      setUserRole(mockUserProfile.role);
      setOrgId(mockUserProfile.orgId);
      return mockUser;
    }

    setError(null);
    setLoading(true);
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      await fetchUserProfile(result.user.uid);
      return result.user;
    } catch (err) {
      setError(getAuthErrorMessage(err.code));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchUserProfile]);

  /**
   * Register a new user
   */
  const register = useCallback(async (email, password, userData) => {
    if (isDemoMode) {
      // Demo mode: simulate registration
      const demoProfile = {
        ...mockUserProfile,
        name: userData.name || 'Demo User',
        email: email,
      };
      setUser({ ...mockUser, email });
      setUserProfile(demoProfile);
      setUserRole(demoProfile.role);
      setOrgId(demoProfile.orgId);
      return mockUser;
    }

    setError(null);
    setLoading(true);
    try {
      // Create auth account
      const result = await createUserWithEmailAndPassword(auth, email, password);

      // Update display name
      if (userData.name) {
        await updateProfile(result.user, { displayName: userData.name });
      }

      // Create user profile in Firestore
      const profile = {
        uid: result.user.uid,
        email: result.user.email,
        name: userData.name || '',
        business_name: userData.business_name || '',
        employee_count: userData.employee_count || 0,
        role: userData.role || USER_ROLES.OWNER,
        org_id: userData.org_id || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      await setDoc(doc(db, 'users', result.user.uid), profile);
      setUserProfile(profile);
      setUserRole(profile.role);
      setOrgId(profile.org_id);

      return result.user;
    } catch (err) {
      setError(getAuthErrorMessage(err.code));
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Sign out
   */
  const logout = useCallback(async () => {
    if (isDemoMode) {
      // Demo mode: just redirect to login
      window.location.href = '/login';
      return;
    }

    setError(null);
    try {
      await signOut(auth);
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.LAST_VIEWED_ORG);
    } catch (err) {
      setError(getAuthErrorMessage(err.code));
      throw err;
    }
  }, []);

  /**
   * Send password reset email
   */
  const resetPassword = useCallback(async (email) => {
    if (isDemoMode) {
      // Demo mode: simulate success
      return;
    }

    setError(null);
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      setError(getAuthErrorMessage(err.code));
      throw err;
    }
  }, []);

  /**
   * Update user profile
   */
  const updateUserProfile = useCallback(async (updates) => {
    if (isDemoMode) {
      setUserProfile((prev) => ({ ...prev, ...updates }));
      return;
    }

    if (!user) throw new Error('No user logged in');

    setError(null);
    try {
      const userRef = doc(db, 'users', user.uid);
      const updatedData = {
        ...updates,
        updated_at: new Date().toISOString(),
      };
      await updateDoc(userRef, updatedData);
      setUserProfile((prev) => ({ ...prev, ...updatedData }));

      // Update Firebase Auth profile if name changed
      if (updates.name) {
        await updateProfile(user, { displayName: updates.name });
      }
    } catch (err) {
      setError('Failed to update profile');
      throw err;
    }
  }, [user]);

  /**
   * Check if user has a specific role
   */
  const hasRole = useCallback((roles) => {
    if (!userRole) return false;
    if (Array.isArray(roles)) {
      return roles.includes(userRole);
    }
    return userRole === roles;
  }, [userRole]);

  /**
   * Check if user is owner or admin
   */
  const isAdmin = useCallback(() => {
    return hasRole([USER_ROLES.OWNER, USER_ROLES.ADMIN]);
  }, [hasRole]);

  /**
   * Check if user is owner
   */
  const isOwner = useCallback(() => {
    return hasRole(USER_ROLES.OWNER);
  }, [hasRole]);

  /**
   * Clear any errors
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Context value
  const value = {
    // State
    user,
    userProfile,
    userRole,
    orgId,
    loading,
    error,
    isDemoMode,

    // Auth methods
    login,
    register,
    logout,
    resetPassword,
    updateUserProfile,

    // Role helpers
    hasRole,
    isAdmin,
    isOwner,

    // Utilities
    clearError,
    refreshProfile: () => user && fetchUserProfile(user.uid),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Custom hook to use auth context
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Convert Firebase auth error codes to user-friendly messages
 */
function getAuthErrorMessage(errorCode) {
  const errorMessages = {
    'auth/email-already-in-use': 'This email is already registered. Please sign in or use a different email.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/operation-not-allowed': 'Email/password accounts are not enabled. Please contact support.',
    'auth/weak-password': 'Password should be at least 8 characters with a mix of letters and numbers.',
    'auth/user-disabled': 'This account has been disabled. Please contact support.',
    'auth/user-not-found': 'No account found with this email. Please check your email or register.',
    'auth/wrong-password': 'Incorrect password. Please try again.',
    'auth/too-many-requests': 'Too many failed attempts. Please wait a few minutes and try again.',
    'auth/network-request-failed': 'Network error. Please check your connection.',
    'auth/popup-closed-by-user': 'Sign-in popup was closed. Please try again.',
    'auth/requires-recent-login': 'Please sign in again to complete this action.',
  };

  return errorMessages[errorCode] || 'An authentication error occurred. Please try again.';
}

export default AuthContext;
